import { useCallback } from 'react';

import { requestAccess as requestAccessService } from '@atlassian/expansion-services/requestAccess';
import { fetchAccessRequestsCapabilities } from '@atlassian/expansion-services/requestAccessCapabilities';

import { useService as useConfluenceService } from '@confluence/expansion-services';

import type { EntryPointId } from '../LoomCrossSellPopupCard/BaseLoomPopupCard.tsx';

type AnalyticsContext = {
	entryPointId: EntryPointId;
	note?: string;
	sourceAdType: string;
};

type AccessRequestCapability = NonNullable<
	Awaited<ReturnType<typeof fetchAccessRequestsCapabilities>>['result']
>;

export const useRequestAccessService = ({
	entryPointId,
	note,
	sourceAdType,
}: AnalyticsContext): (() => Promise<boolean>) => {
	const requestAccess = useConfluenceService(requestAccessService);

	return useCallback(async (): Promise<boolean> => {
		const { error, result } = await requestAccess({
			product: 'loom',
			integration: entryPointId,
			note,
			touchpoint: 'Crossjoin Nudge',
			getAdditionalAttributes: (requestAccessResult) => ({
				adType: sourceAdType,
				entrypoint: requestAccessResult?.integration,
				result: requestAccessResult?.result,
				success: requestAccessResult?.result === 'PENDING_REQUEST_CREATED',
			}),
		});

		if (error) {
			// The platform service handles error analytics; we surface false to callers
			// instead of re-throwing to match the platform service's error contract.
			return false;
		}

		return result === 'PENDING_REQUEST_CREATED';
	}, [entryPointId, note, requestAccess, sourceAdType]);
};

export const useFetchRequestAccessCapability = ({
	entryPointId,
	sourceAdType,
}: AnalyticsContext): (() => Promise<AccessRequestCapability | undefined>) => {
	const capabilitiesService = useConfluenceService(fetchAccessRequestsCapabilities);

	return useCallback(async () => {
		const { error, result } = await capabilitiesService({
			product: 'loom',
			integration: entryPointId,
			touchpoint: 'Crossjoin Nudge',
			getAdditionalAttributes: (capabilitiesResult) => ({
				adType: sourceAdType,
				entrypoint: capabilitiesResult?.integration,
				resource: capabilitiesResult?.resource,
			}),
		});

		if (error) {
			return undefined;
		}

		return result ?? undefined;
	}, [capabilitiesService, entryPointId, sourceAdType]);
};
